import { useEffect, useState } from "react";
import { useYacht } from "../../context/YachtContext";
import { useAuth } from "../../context/AuthContext";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Wrench, ClipboardList, Package, Wallet, FileText, Image as ImageIcon,
  AlertTriangle, ArrowUpRight, ArrowRight, BookOpen, Ship, Check, Eye, ListChecks, Receipt, RotateCw, MessageSquare, ChevronDown, ChevronUp, ShieldAlert, CalendarClock,
} from "lucide-react";
import { useRights } from "../../lib/useRights";
import { AuthImage } from "../../components/FileUploader";
import { Button } from "../../components/ui/button";
import YachtDetailsDialog from "../../components/YachtDetailsDialog";
import { JobDetailModal } from "./Maintenance";
import ProviderHome from "./ProviderHome";
import GettingStarted from "../../components/tour/GettingStarted";

// Display labels for yacht-membership roles. "provider" is an internal role
// name; users should always see "Supplier".
const ROLE_LABELS = { provider: "Supplier" };
const roleLabel = (r) => ROLE_LABELS[r] || r || "member";

/* Escalated approval requests ("above my limit") land here as one-tap
 * Approve / Dismiss banners. Only recipients (owner + managers) have these
 * notification rows, so no extra role gating is needed. */
const APPROVE_CALLS = {
  quote: (id) => api.post(`/quotes/${id}/accept`),
  invoice: (id) => api.post(`/invoices/${id}/status`, { status: "paid" }),
  pettycash: (id) => api.post(`/petty-cash/${id}/approve`, { status: "approved" }),
  final_approval: (id) => api.patch(`/maintenance/${id}/status`, { status: "confirmed" }),
};
function ApprovalRequestsBanner() {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notifications");
        setItems((data || []).filter((n) => n.action?.kind && APPROVE_CALLS[n.action.kind]));
      } catch { /* non-fatal */ }
    })();
  }, []);

  const dismiss = async (n) => {
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    try { await api.post(`/notifications/${n.id}/dismiss`); } catch { /* swallow */ }
  };
  const approve = async (n) => {
    setBusyId(n.id);
    try {
      await APPROVE_CALLS[n.action.kind](n.action.id);
      toast.success("Approved");
      await dismiss(n);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally { setBusyId(null); }
  };

  if (items.length === 0) return null;
  return (
    <div className="mt-6 space-y-2" data-testid="approval-requests-banner">
      {items.map((n) => (
        <div key={n.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid={`approval-request-${n.id}`}>
          <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0" />
          <div className="min-w-0 flex-1 text-sm text-amber-900">
            <span className="font-medium">{n.yacht_name ? `${n.yacht_name} — ` : ""}Approval requested:</span> {n.summary}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" className="bg-ink hover:bg-black h-8" disabled={busyId === n.id}
              onClick={() => approve(n)} data-testid={`approve-request-${n.id}`}>
              {busyId === n.id ? "Approving…" : "Approve"}
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-zinc-500" onClick={() => dismiss(n)} data-testid={`dismiss-request-${n.id}`}>
              Dismiss
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
const shortDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return String(iso).slice(0, 10); }
};

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Result of an email "Approve now" magic link (?approval=…) — show a toast.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const r = searchParams.get("approval");
    if (!r) return;
    const msgs = {
      approved: ["success", "Approved — done from your email link"],
      already: ["success", "Already handled — nothing left to approve"],
      expired: ["error", "That approval link has expired — approve it from the banner below"],
      invalid: ["error", "That approval link is no longer valid"],
      failed: ["error", "The approval could not be completed — try it from the app"],
    };
    const [kind, msg] = msgs[r] || msgs.invalid;
    (kind === "success" ? toast.success : toast.error)(msg);
    const next = new URLSearchParams(searchParams);
    next.delete("approval");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const { activeYacht, yachts, switchYacht } = useYacht();
  const { user } = useAuth();
  const { role: yachtRole, can } = useRights();
  const canCreate = ["owner", "manager", "captain"].includes(user?.role);
  const [stats, setStats] = useState(null);
  const [assignments, setAssignments] = useState(null);
  const [dueChecklists, setDueChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [supSummary, setSupSummary] = useState({});
  const [ownerSummary, setOwnerSummary] = useState({});
  const [detailsYacht, setDetailsYacht] = useState(null);
  const isProvider = yachtRole === "provider";
  const isSupplier = !!user?.is_supplier;
  // Refresh per-yacht summaries every 2 minutes so unread message counts stay
  // current without a manual reload.
  const [pollTick, setPollTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPollTick((t) => t + 1), 120000);
    return () => clearInterval(id);
  }, []);

  // Supplier per-yacht summary (primary contact + open tasks / quote counts)
  // — same source the Yachts page uses so both surfaces show identical info.
  useEffect(() => {
    if (!isSupplier) return;
    (async () => {
      try {
        const { data } = await api.get("/supplier/yacht-summary");
        setSupSummary(Object.fromEntries((data || []).map((r) => [r.yacht_id, r])));
      } catch { /* non-fatal */ }
    })();
  }, [isSupplier, yachts.length, pollTick]);

  // Owner/crew per-yacht summary — open task + periodic-task counts for the
  // yacht cards (so each card mirrors the operational dashboard tiles).
  useEffect(() => {
    if (isSupplier) return;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/yacht-summary");
        setOwnerSummary(Object.fromEntries((data || []).map((r) => [r.yacht_id, r])));
      } catch { /* non-fatal */ }
    })();
  }, [isSupplier, yachts.length, pollTick]);

  // Service-provider accounts with no yacht assignment land on a dedicated
  // home page — we still need to call every hook below before bailing, so
  // we just compute the flag here and short-circuit at the bottom.
  const showProviderHome = (
    user?.account_kind === "provider" && (!yachts || yachts.length === 0)
  );

  useEffect(() => {
    if (!activeYacht?.id) { setStats(null); setAssignments(null); setDueChecklists([]); return; }
    setLoading(true);
    // Provider doesn't have dashboard:read — fetch their own assignments instead.
    if (isProvider) {
      api.get(`/me/assignments?yacht_id=${activeYacht.id}`)
        .then((r) => { setAssignments(r.data); setStats(null); })
        .catch(() => setAssignments(null))
        .finally(() => setLoading(false));
    } else {
      api.get(`/dashboard?yacht_id=${activeYacht.id}`)
        .then((r) => { setStats(r.data); setAssignments(null); })
        .catch(() => setStats(null))
        .finally(() => setLoading(false));
      // Recurring checklists due this period — independent fetch so a slow
      // /dashboard call doesn't delay the rest of the page.
      api.get(`/checklists/due?yacht_id=${activeYacht.id}`)
        .then((r) => setDueChecklists(r.data || []))
        .catch(() => setDueChecklists([]));
    }
    // Depend on the yacht *id*, not the whole object — YachtContext may
    // hand us a fresh object reference whenever its list reloads (route
    // change, 403-refresh from axios interceptor, etc.), but the dashboard
    // only needs to refetch when the user actually switches yachts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeYacht?.id, isProvider]);

  if (!activeYacht) {
    return (
      <div className="p-10 max-w-3xl">
        <PageHeader overline={t("nav.dashboard")} title={t("dashboard.welcome_aboard")} />
        <div className="mt-8 rounded-lg border border-zinc-200 bg-white p-8" data-testid="empty-state">
          <div className="font-display text-xl font-medium">
            {canCreate ? t("dashboard.no_yacht_title_owner") : t("dashboard.no_yacht_title_crew")}
          </div>
          <p className="text-sm text-zinc-600 mt-2 max-w-md">
            {canCreate
              ? t("dashboard.no_yacht_body_owner")
              : t("dashboard.no_yacht_body_crew")}
          </p>
          {canCreate && (
            <Link
              to="/app/yachts"
              data-testid="cta-create-yacht"
              className="mt-5 inline-flex items-center rounded-md bg-ink text-white px-4 py-2 text-sm font-medium hover:bg-black"
            >
              {t("dashboard.create_yacht")} <ArrowUpRight className="h-4 w-4 ml-2" />
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (showProviderHome) {
    return <ProviderHome />;
  }

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <PageHeader
        overline={t("dashboard.fleet")}
        title={t("nav.yachts")}
      />

      {isSupplier && (
        <SupplierOutstandingSection
          yachts={yachts}
          summaries={supSummary}
          onOpenYacht={(y) => { switchYacht(y); navigate("/app/supplier/invoices"); }}
        />
      )}

      <ApprovalRequestsBanner />

      <div
        className={isSupplier
          ? "mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          : "mt-6 grid grid-cols-1 min-[440px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"}
        data-testid="my-yachts-grid"
      >
        {yachts.map((y) => (
          isSupplier ? (
            <SupplierYachtCard
              key={y.id}
              yacht={y}
              active={y.id === activeYacht.id}
              summary={supSummary[y.id]}
              onActivate={() => switchYacht(y)}
              onDetails={() => setDetailsYacht(y)}
              onOpenTasks={() => { switchYacht(y); navigate("/app/supplier/tasks"); }}
              onOpenQuotes={() => { switchYacht(y); navigate("/app/supplier"); }}
              onOpenInvoices={() => { switchYacht(y); navigate("/app/supplier/invoices"); }}
            />
          ) : (
            <YachtTile
              key={y.id}
              yacht={y}
              active={y.id === activeYacht.id}
              summary={ownerSummary[y.id]}
              onClick={() => switchYacht(y)}
              onOpenTasks={() => { switchYacht(y); navigate("/app/tasks?status=open"); }}
              onOpenPeriodic={() => { switchYacht(y); navigate("/app/maintenance"); }}
              onReviewQuotes={() => { switchYacht(y); navigate("/app/tasks"); }}
              onOpenInvoices={() => { switchYacht(y); navigate("/app/invoices"); }}
            />
          )
        ))}
      </div>

      <YachtDetailsDialog yacht={detailsYacht} onClose={() => setDetailsYacht(null)} />

      <UnreadMessagesPanel navigate={navigate} />

      {!isProvider && (
        <GettingStarted yachtId={activeYacht.id} role={yachtRole} userId={user?.id} />
      )}

      <div className="mt-10 border-t border-zinc-200 pt-8">
      <PageHeader
        overline={`${activeYacht.name} · ${roleLabel(activeYacht.my_role)}`}
        title={isProvider ? "Assigned tasks to this yacht" : t("dashboard.operational_dashboard")}
      />

      {loading && <div className="text-sm text-zinc-500 mt-8">{t("common.loading")}</div>}

      {isProvider && assignments && (
        <AssignmentsView
          assignments={assignments}
          yacht={activeYacht}
          userId={user?.id}
          can={can}
          onRefresh={async () => {
            try {
              const r = await api.get(`/me/assignments?yacht_id=${activeYacht.id}`);
              setAssignments(r.data);
            } catch { /* ignore */ }
          }}
        />
      )}

      {stats && (
        <>
          {/* Stat grid. Bumped from 4 to 5 columns at md+ so the new
              invoices tile fits next to the existing four without
              squeezing them. The xl breakpoint already balances 5 wide. */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="dashboard-stats">
            <StatCard icon={Wrench} label={t("dashboard.stat_open_tasks")} value={stats.open_maintenance} tone={stats.open_maintenance > 5 ? "warn" : ""} to="/app/tasks?status=open" testid="stat-tasks" />
            <StatCard icon={RotateCw} label={t("dashboard.stat_open_periodic_tasks")} value={stats.open_periodic_tasks ?? 0} tone={(stats.open_periodic_tasks ?? 0) > 5 ? "warn" : ""} to="/app/maintenance" testid="stat-periodic-tasks" />
            <StatCard icon={Package} label={t("dashboard.stat_low_stock")} value={stats.low_stock_count} tone={stats.low_stock_count > 0 ? "warn" : ""} to="/app/inventory?filter=low-stock" testid="stat-inventory" />
            <StatCard icon={Wallet} label={t("dashboard.stat_petty_cash_balance")} value={`AED ${stats.petty_cash_balance.toFixed(0)}`} to="/app/petty-cash" testid="stat-petty-cash" />
            <StatCard
              icon={Receipt}
              label={t("dashboard.stat_outstanding_invoices")}
              value={`AED ${(stats.invoices_outstanding_amount ?? 0).toFixed(0)}`}
              tone={(stats.invoices_overdue_count ?? 0) > 0 ? "danger" : ((stats.invoices_outstanding_count ?? 0) > 0 ? "warn" : "")}
              to="/app/invoices?status=outstanding"
              testid="stat-invoices"
            />
          </div>

          {(stats.awaiting_my_confirmation ?? 0) > 0 && (
            <Link
              to="/app/tasks?status=awaiting_confirmation"
              className="mt-6 flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 hover:bg-emerald-100 transition-colors"
              data-testid="awaiting-confirmation-banner"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="grid place-items-center w-9 h-9 rounded-full bg-emerald-100 shrink-0">
                  <Check className="w-5 h-5 text-emerald-700" />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-emerald-900" data-testid="awaiting-confirmation-title">
                    {t("dashboard.awaiting_confirmation", { count: stats.awaiting_my_confirmation })}
                  </div>
                  <div className="text-[11px] text-emerald-700">
                    {t("dashboard.awaiting_confirmation_sub")}
                  </div>
                </div>
              </div>
              <span className="text-xs font-medium text-emerald-700 whitespace-nowrap inline-flex items-center gap-1">
                {t("common.review")} <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          )}


          {dueChecklists.length > 0 && (
            <div className="mt-6">
              <Panel
                title={t("dashboard.panel_checklists_due", { count: dueChecklists.length })}
                icon={ListChecks}
                cta={
                  <Link to="/app/checklists" className="text-xs text-zinc-500 hover:text-ink">
                    {t("common.view_all")}
                  </Link>
                }
              >
                <ul className="divide-y divide-zinc-200" data-testid="due-checklists">
                  {dueChecklists.map((c) => (
                    <li
                      key={c.id}
                      className="py-2 flex items-center justify-between gap-3"
                      data-testid={`due-checklist-${c.id}`}
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{c.name}</div>
                        <div className="text-[11px] text-zinc-500 flex items-center gap-1.5 mt-0.5">
                          <span
                            className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-medium ${
                              c.frequency === "daily" ? "bg-sky-50 text-sky-700"
                              : c.frequency === "weekly" ? "bg-emerald-50 text-emerald-700"
                              : c.frequency === "biweekly" ? "bg-amber-50 text-amber-700"
                              : "bg-purple-50 text-purple-700"
                            }`}
                          >
                            {c.frequency}
                          </span>
                          <span>{c.period_label}</span>
                          <span className="text-zinc-300">·</span>
                          <span>{(c.items || []).length} items</span>
                        </div>
                      </div>
                      <Link
                        to={`/app/checklists?run=${c.id}`}
                        className="text-[11px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-full px-3 py-1 transition-colors whitespace-nowrap"
                        data-testid={`run-due-${c.id}`}
                      >
                        Run now
                      </Link>
                    </li>
                  ))}
                </ul>
              </Panel>
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Panel
              title={stats.ongoing_tasks_scope === "mine" ? t("dashboard.panel_ongoing_tasks_mine") : t("dashboard.panel_ongoing_tasks")}
              cta={<Link to="/app/tasks?status=open" className="text-xs text-zinc-500 hover:text-ink">{t("common.view_all")}</Link>}
            >
              {(stats.ongoing_tasks ?? 0) === 0 ? (
                <Empty label={stats.ongoing_tasks_scope === "mine" ? t("dashboard.panel_ongoing_empty_mine") : t("dashboard.panel_ongoing_empty_yacht")} />
              ) : (
                <Link
                  to="/app/tasks?status=open"
                  className="inline-flex items-baseline gap-2 text-3xl font-display font-medium hover:text-ink"
                  data-testid="dashboard-ongoing-tasks"
                >
                  {stats.ongoing_tasks}
                  <span className="text-xs text-zinc-500 font-sans">{t("dashboard.panel_open_label")}</span>
                </Link>
              )}
            </Panel>

            <Panel title={t("dashboard.panel_expiring_docs")} cta={<Link to="/app/documents" className="text-xs text-zinc-500 hover:text-ink">{t("common.view_all")}</Link>}>
              {stats.expiring_documents.length === 0 ? (
                <Empty label={t("dashboard.panel_expiring_docs_empty")} />
              ) : (
                <ul className="text-sm divide-y divide-zinc-200">
                  {stats.expiring_documents.slice(0, 4).map((d) => (
                    <li key={d.id} className="py-2 flex justify-between items-center">
                      <div className="truncate">{d.name}</div>
                      <div className="text-xs text-amber-600">{(d.expires_at || "").slice(0, 10)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel title={t("dashboard.panel_petty_cash_needs_you")} cta={<Link to="/app/petty-cash" className="text-xs text-zinc-500 hover:text-ink">{t("common.review")}</Link>}>
              {(() => {
                const mine = (stats.pc_awaiting_my_receipt || 0) + (stats.pc_awaiting_my_approval || 0);
                if (mine > 0) {
                  return (
                    <ul className="space-y-2" data-testid="pc-my-actions">
                      {stats.pc_awaiting_my_receipt > 0 && (
                        <li className="flex items-center justify-between rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm">
                          <span className="text-sky-900">
                            {t("dashboard.topups_to_ack", { count: stats.pc_awaiting_my_receipt })}
                          </span>
                          <Link to="/app/petty-cash" className="text-xs text-sky-700 hover:text-sky-900">{t("common.open")} →</Link>
                        </li>
                      )}
                      {stats.pc_awaiting_my_approval > 0 && (
                        <li className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
                          <span className="text-amber-900">
                            {t("dashboard.expenses_to_approve", { count: stats.pc_awaiting_my_approval })}
                          </span>
                          <Link to="/app/petty-cash" className="text-xs text-amber-700 hover:text-amber-900">{t("common.open")} →</Link>
                        </li>
                      )}
                    </ul>
                  );
                }
                if ((stats.pending_petty_cash || 0) > 0) {
                  return (
                    <div className="flex items-end gap-2">
                      <div className="text-3xl font-display font-medium">{stats.pending_petty_cash}</div>
                      <div className="text-sm text-zinc-500 pb-1">{t("dashboard.pending_elsewhere")}</div>
                    </div>
                  );
                }
                return <Empty label={t("dashboard.panel_petty_cash_empty")} />;
              })()}
            </Panel>
          </div>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Panel
              title={t("dashboard.panel_low_stock")}
              icon={AlertTriangle}
              cta={<ReviewLink to="/app/inventory?filter=low-stock" testid="review-low-stock" />}
            >
              {stats.low_stock_items.length === 0 ? (
                <Empty label={t("dashboard.panel_low_stock_empty")} />
              ) : (
                <ul className="divide-y divide-zinc-200">
                  {stats.low_stock_items.slice(0, 6).map((it) => (
                    <li key={it.id} className="py-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm">{it.item_name || it.name}</div>
                        <div className="text-xs text-zinc-500 truncate">{it.category}</div>
                      </div>
                      <span className="shrink-0 font-mono text-xs">{it.quantity}/{it.min_stock}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel
              title={t("dashboard.panel_expiring")}
              icon={CalendarClock}
              cta={<ReviewLink to="/app/inventory?filter=expiring" testid="review-expiring" />}
            >
              {(stats.expiring_inventory || []).length === 0 ? (
                <Empty label={t("dashboard.panel_expiring_empty")} />
              ) : (
                <ul className="divide-y divide-zinc-200" data-testid="expiring-inv-list">
                  {(stats.expiring_inventory || []).slice(0, 6).map((it) => {
                    const days = Math.round((new Date(it.expiry_date + "T00:00:00") - new Date().setHours(0, 0, 0, 0)) / 86400000);
                    const expired = days < 0;
                    return (
                      <li key={it.id} className="py-2 flex items-center justify-between gap-3" data-testid={`expiring-inv-row-${it.id}`}>
                        <div className="min-w-0">
                          <div className="truncate text-sm">{it.name}</div>
                          <div className="text-xs text-zinc-500 truncate">
                            {it.category}{it.location ? ` · ${it.location}` : ""}
                          </div>
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${expired ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                          {expired ? t("dashboard.expiring_expired") : days === 0 ? t("dashboard.expiring_today") : `${days}d`}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Panel>

            <Panel
              title={t("dashboard.panel_recent_maint")}
              icon={Wrench}
              cta={<ReviewLink to="/app/maintenance" testid="review-maintenance" />}
            >
              {stats.recent_maintenance.length === 0 ? (
                <Empty label={t("dashboard.panel_recent_maint_empty")} />
              ) : (
                <ul className="divide-y divide-zinc-200">
                  {stats.recent_maintenance.slice(0, 6).map((m) => (
                    <li key={m.id} className="py-2 flex items-center justify-between gap-3">
                      <Link
                        to={`/app/maintenance?open=${m.id}`}
                        className="min-w-0 flex-1 hover:underline"
                        data-testid={`recent-maint-row-${m.id}`}
                      >
                        <div className="truncate text-sm">{m.title}</div>
                        <div className="text-xs text-zinc-500">
                          {m.system_category ? `${m.system_category} · ` : ""}
                          <span className="text-zinc-400">{shortDate(m.created_at)}</span>
                        </div>
                      </Link>
                      <div className="flex items-center gap-2 shrink-0">
                        {(m.status === "waiting_approval" || m.status === "waiting_prepay") && (
                          <Link
                            to={`/app/maintenance?open=${m.id}`}
                            className="text-[11px] font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-full px-2.5 py-0.5 transition-colors"
                            data-testid={`review-maint-${m.id}`}
                          >
                            {t("common.review")}
                          </Link>
                        )}
                        <StatusBadge value={m.status} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel
              title={t("dashboard.panel_recent_logbook")}
              icon={BookOpen}
              cta={<ReviewLink to="/app/logbook" testid="review-logbook" />}
            >
              {stats.recent_logs.length === 0 ? (
                <Empty label={t("dashboard.panel_recent_logbook_empty")} />
              ) : (
                <ul className="divide-y divide-zinc-200">
                  {stats.recent_logs.slice(0, 5).map((l) => (
                    <li key={l.id} className="py-2 text-sm">
                      <Link
                        to={`/app/logbook?open=${l.id}`}
                        className="block hover:underline"
                        data-testid={`recent-log-row-${l.id}`}
                      >
                        <span className="uppercase text-[10px] tracking-[0.15em] text-zinc-500 mr-2">{l.log_type}</span>
                        {l.notes || l.location || "—"}
                        <span className="text-xs text-zinc-400 ml-2">{shortDate(l.log_date || l.created_at)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel
              title={t("dashboard.panel_recent_photos")}
              icon={ImageIcon}
              cta={<ReviewLink to="/app/photos" testid="review-photos" />}
            >
              {stats.recent_photos.length === 0 ? (
                <Empty label={t("dashboard.panel_recent_photos_empty")} />
              ) : (
                <div className="grid grid-cols-4 gap-2" data-testid="dashboard-recent-photos-grid">
                  {stats.recent_photos.slice(0, 4).map((p) => (
                    <Link
                      key={p.id}
                      to="/app/photos"
                      className="block aspect-square rounded overflow-hidden bg-zinc-100 border border-zinc-200 hover:border-zinc-400 transition-colors"
                      data-testid={`recent-photo-${p.id}`}
                    >
                      <AuthImage
                        path={p.file_path}
                        alt={p.caption || ""}
                        className="w-full h-full object-cover"
                      />
                    </Link>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        </>
      )}
      </div>
    </div>
  );
}

export function PageHeader({ overline, title, right }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase font-semibold text-zinc-500">{overline}</div>
        <h1 className="font-display text-3xl lg:text-4xl font-medium tracking-tight mt-1">{title}</h1>
      </div>
      {right}
    </div>
  );
}

function SupplierOutstandingSection({ yachts, summaries, onOpenYacht }) {
  const [open, setOpen] = useState(false);
  const rows = yachts
    .map((y) => ({ yacht: y, s: summaries[y.id] }))
    .filter((r) => (r.s?.unpaid_count || 0) > 0);
  if (rows.length === 0) return null;
  const total = rows.reduce((sum, r) => sum + (r.s.unpaid_amount || 0), 0);
  const count = rows.reduce((sum, r) => sum + (r.s.unpaid_count || 0), 0);
  const fmt = (n) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="mt-6 rounded-xl border border-orange-200 bg-orange-50/60 overflow-hidden" data-testid="supplier-outstanding-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-orange-50"
        data-testid="supplier-outstanding-toggle"
      >
        <div className="flex items-center gap-2 text-orange-800">
          <Wallet className="h-4 w-4" />
          <span className="font-medium text-sm">Outstanding invoices</span>
          <span className="text-[11px] text-orange-700">{fmt(total)} · {count} invoice{count > 1 ? "s" : ""} · {rows.length} yacht{rows.length > 1 ? "s" : ""}</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-orange-700" /> : <ChevronDown className="h-4 w-4 text-orange-700" />}
      </button>
      {open && (
        <div className="border-t border-orange-200 divide-y divide-orange-100" data-testid="supplier-outstanding-list">
          {rows.map(({ yacht, s }) => (
            <button
              key={yacht.id}
              type="button"
              onClick={() => onOpenYacht(yacht)}
              className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left bg-white hover:bg-orange-50/50"
              data-testid={`supplier-outstanding-yacht-${yacht.id}`}
            >
              <span className="text-sm font-medium truncate">{yacht.name}</span>
              <span className="text-sm text-orange-800 font-semibold shrink-0">{fmt(s.unpaid_amount)} <span className="text-[11px] font-normal text-zinc-500">({s.unpaid_count})</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SupplierYachtCard({ yacht, active, summary, onActivate, onDetails, onOpenTasks, onOpenQuotes, onOpenInvoices }) {
  const subtitle = [yacht.brand, yacht.model, yacht.length_m && `${yacht.length_m}m`]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      onClick={onActivate}
      data-testid={`supplier-yacht-card-${yacht.id}`}
      className={`rounded-md border bg-white overflow-hidden cursor-pointer transition-colors ${
        active ? "border-ink ring-1 ring-ink" : "border-zinc-200 hover:border-zinc-400 hover:shadow-sm"
      }`}
    >
      <div className="p-4 pb-0">
        {yacht.cover_image ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${yacht.cover_image}`}
            alt={yacht.name}
            className="rounded-md border border-zinc-200 block"
            style={{ width: 200, height: "auto" }}
            data-testid={`supplier-yacht-cover-${yacht.id}`}
          />
        ) : (
          <div
            className="rounded-md border border-zinc-200 bg-zinc-50 grid place-items-center text-zinc-300"
            style={{ width: 200, height: 125 }}
          >
            <Ship className="h-8 w-8" />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display text-lg font-medium leading-tight truncate">{yacht.name}</div>
            {subtitle && <div className="text-xs text-zinc-500 mt-0.5 truncate">{subtitle}</div>}
          </div>
          <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full border border-zinc-200 uppercase tracking-wider">
            Supplier
          </span>
        </div>
        <div className="mt-2 space-y-0.5 text-[12px] text-zinc-600" data-testid={`supplier-yacht-summary-${yacht.id}`}>
          {summary?.contact?.name && (
            <div className="text-zinc-500">Contact: <span className="text-zinc-800 font-medium">{summary.contact.name}</span></div>
          )}
          <div className="flex flex-wrap gap-x-4">
            <span>Open tasks:{" "}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenTasks(); }}
                className="font-semibold text-red-600 hover:text-red-700 hover:underline"
                data-testid={`supplier-yacht-open-tasks-${yacht.id}`}
              >
                {summary?.open_tasks ?? 0}
              </button>
            </span>
            <span>Open quote requests:{" "}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenQuotes(); }}
                className="font-semibold text-red-600 hover:text-red-700 hover:underline"
                data-testid={`supplier-yacht-open-quotes-${yacht.id}`}
              >
                {summary?.open_quotes ?? 0}
              </button>
            </span>
            <span>Awaiting decision:{" "}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenQuotes(); }}
                className="font-semibold text-amber-600 hover:text-amber-700 hover:underline"
                data-testid={`supplier-yacht-awaiting-${yacht.id}`}
              >
                {summary?.awaiting_decision ?? 0}
              </button>
            </span>
          </div>
          {summary?.unpaid_count > 0 && (
            <div className="flex items-center gap-1 text-orange-700 font-medium pt-0.5">
              <Wallet className="h-3 w-3" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenInvoices(); }}
                className="hover:underline"
                data-testid={`supplier-yacht-unpaid-${yacht.id}`}
              >
                Waiting for payment: {(summary.unpaid_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({summary.unpaid_count})
              </button>
            </div>
          )}
          {summary?.invoice_unread > 0 && (
            <div className="flex items-center gap-1 text-red-600 font-medium pt-0.5">
              <MessageSquare className="h-3 w-3" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenInvoices(); }}
                className="hover:underline"
                data-testid={`supplier-yacht-invoice-unread-${yacht.id}`}
              >
                {summary.invoice_unread} new invoice message{summary.invoice_unread > 1 ? "s" : ""}
              </button>
            </div>
          )}
        </div>
        <div className="mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onDetails(); }}
            data-testid={`supplier-yacht-details-${yacht.id}`}
          >
            <Ship className="h-3.5 w-3.5 mr-1.5" /> Details
          </Button>
        </div>
      </div>
    </div>
  );
}


function UnreadMessagesPanel({ navigate }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try { const { data } = await api.get("/messages/unread"); if (!cancelled) setItems(data || []); }
      catch { /* non-fatal */ }
    };
    load();
    const id = setInterval(load, 120000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  if (items.length === 0) return null;
  const fmtTime = (s) => {
    if (!s) return "";
    try { return new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };
  const open = (it) => navigate(it.target === "supplier" ? "/app/supplier" : "/app/tasks");
  return (
    <section className="mt-8" data-testid="dashboard-new-messages">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="h-4 w-4 text-red-600" />
        <h2 className="text-base font-semibold">New messages</h2>
        <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-semibold bg-red-600 text-white" data-testid="new-messages-count">
          {items.reduce((a, i) => a + (i.unread || 0), 0)}
        </span>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <button
            key={it.quote_id}
            onClick={() => open(it)}
            className="w-full text-left rounded-md border border-zinc-200 bg-white p-3 hover:border-zinc-400 hover:shadow-sm transition-colors flex items-start gap-3"
            data-testid={`new-message-${it.quote_id}`}
          >
            <div className="mt-0.5 shrink-0 h-8 w-8 rounded-full bg-red-50 border border-red-100 grid place-items-center">
              <MessageSquare className="h-4 w-4 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">{it.sender_name || (it.target === "supplier" ? "Yacht crew" : it.supplier_name)}</span>
                {it.unread > 1 && <span className="text-[10px] font-semibold text-red-600">+{it.unread}</span>}
                <span className="ml-auto text-[11px] text-zinc-400 shrink-0">{fmtTime(it.at)}</span>
              </div>
              <div className="text-xs text-zinc-500 truncate">
                {it.task_title}{it.yacht_name ? ` · ${it.yacht_name}` : ""}
              </div>
              <div className="text-sm text-zinc-700 truncate mt-0.5">{it.text}</div>
            </div>
            <ArrowRight className="h-4 w-4 text-zinc-300 shrink-0 self-center" />
          </button>
        ))}
      </div>
    </section>
  );
}

function YachtTile({ yacht, active, summary, onClick, onOpenTasks, onOpenPeriodic, onReviewQuotes, onOpenInvoices }) {
  const { t } = useTranslation();
  const subtitle = [yacht.brand, yacht.model, yacht.length_m && `${yacht.length_m}m`]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      onClick={onClick}
      data-testid={`yacht-tile-${yacht.id}`}
      className={`group text-left rounded-md border bg-white overflow-hidden transition-colors cursor-pointer ${
        active
          ? "border-ink ring-1 ring-ink"
          : "border-zinc-200 hover:border-zinc-400"
      }`}
    >
      <div className="aspect-[16/10] bg-zinc-50 border-b border-zinc-200 grid place-items-center overflow-hidden">
        {yacht.cover_image ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${yacht.cover_image}`}
            alt={yacht.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <Ship className="h-6 w-6 text-zinc-300" />
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{yacht.name}</div>
            {subtitle && (
              <div className="text-[11px] text-zinc-500 truncate mt-0.5">{subtitle}</div>
            )}
          </div>
          {active && (
            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
              <Check className="h-3 w-3" /> {t("dashboard.active_badge")}
            </span>
          )}
        </div>
        <div className="mt-2 text-[10px] uppercase tracking-wider text-zinc-500">
          {roleLabel(yacht.my_role)}
        </div>
        <div className="mt-2 space-y-0.5 text-[11px] text-zinc-600" data-testid={`yacht-tile-summary-${yacht.id}`}>
          <div>{t("dashboard.stat_open_tasks")}:{" "}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenTasks(); }}
              className="font-semibold text-red-600 hover:text-red-700 hover:underline px-1.5 py-1 -m-1"
              data-testid={`yacht-tile-open-tasks-${yacht.id}`}
            >
              {summary?.open_tasks ?? 0}
            </button>
          </div>
          <div>{t("dashboard.stat_open_periodic_tasks")}:{" "}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenPeriodic(); }}
              className="font-semibold text-red-600 hover:text-red-700 hover:underline px-1.5 py-1 -m-1"
              data-testid={`yacht-tile-open-periodic-${yacht.id}`}
            >
              {summary?.open_periodic_tasks ?? 0}
            </button>
          </div>
          {summary?.quotes_to_review > 0 && (
            <div className="flex items-center gap-1 text-amber-700 font-medium">
              <FileText className="h-3 w-3" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onReviewQuotes(); }}
                className="hover:underline"
                data-testid={`yacht-tile-quotes-review-${yacht.id}`}
              >
                {summary.quotes_to_review} new quote{summary.quotes_to_review > 1 ? "s" : ""} to review
              </button>
            </div>
          )}
          {summary?.unread_messages > 0 && (
            <div className="flex items-center gap-1 text-red-600 font-medium">
              <MessageSquare className="h-3 w-3" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenTasks(); }}
                className="hover:underline"
                data-testid={`yacht-tile-unread-${yacht.id}`}
              >
                {summary.unread_messages} new message{summary.unread_messages > 1 ? "s" : ""}
              </button>
            </div>
          )}
          {summary?.unpaid_count > 0 && (
            <div className="flex items-center gap-1 text-orange-700 font-medium">
              <Wallet className="h-3 w-3" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenInvoices(); }}
                className="hover:underline"
                data-testid={`yacht-tile-unpaid-${yacht.id}`}
              >
                Waiting for payment: {(summary.unpaid_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({summary.unpaid_count})
              </button>
            </div>
          )}
          {summary?.invoice_unread > 0 && (
            <div className="flex items-center gap-1 text-red-600 font-medium">
              <MessageSquare className="h-3 w-3" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenInvoices(); }}
                className="hover:underline"
                data-testid={`yacht-tile-invoice-unread-${yacht.id}`}
              >
                {summary.invoice_unread} new invoice message{summary.invoice_unread > 1 ? "s" : ""}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AssignmentsView({ assignments, yacht, userId, can, onRefresh }) {
  const [openJob, setOpenJob] = useState(null);
  const items = [...(assignments?.maintenance || []), ...(assignments?.tasks || [])];
  // The modal resolves the assignee label from a members list; the provider
  // has no crew:read, so synthesise a single "You" entry.
  const pseudoMembers = [{ user_id: userId, role: "provider", name: "You" }];
  if (items.length === 0) {
    return (
      <div className="mt-8 rounded-md border border-zinc-200 bg-white p-8 text-center">
        <ClipboardList className="h-6 w-6 mx-auto text-zinc-300" />
        <div className="mt-2 text-sm text-zinc-700">Nothing assigned to this yacht yet.</div>
        <div className="mt-1 text-[12px] text-zinc-500">
          The captain or manager of this yacht hasn't allocated any jobs to you.
          You'll see them here as soon as they do.
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="mt-6 rounded-md border border-zinc-200 bg-white divide-y divide-zinc-200" data-testid="provider-assignments">
        {items.map((m) => (
          <div key={m.id} className="p-3 flex items-start justify-between gap-3" data-testid={`assign-item-${m.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{m.title}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                {m.system_category || m.priority}
                {m.due_date && <> · due {(m.due_date || "").slice(0, 10)}</>}
              </div>
              {m.description && (
                <div className="text-[12px] text-zinc-600 mt-1 line-clamp-2">{m.description}</div>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <StatusBadge value={m.status} />
              <button
                type="button"
                onClick={() => setOpenJob(m)}
                className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"
                data-testid={`open-assign-${m.id}`}
                title="Open task"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {openJob && (
        <JobDetailModal
          job={openJob}
          inventory={[]}
          members={pseudoMembers}
          userId={userId}
          userRole="provider"
          can={can}
          onClose={() => setOpenJob(null)}
          onRefresh={onRefresh}
        />
      )}
    </>
  );
}

function StatCard({ icon: Icon, label, value, tone = "", testid, to }) {
  const ring =
    tone === "danger" ? "border-red-200" : tone === "warn" ? "border-amber-200" : "border-zinc-200";
  // When a `to` prop is supplied the entire card becomes a Link to that
  // module — meets the operator request "click on the category, go to
  // this sub-system". Hover state + cursor make the affordance obvious.
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <Icon className="h-4 w-4 text-zinc-400" />
      </div>
      <div className="mt-3 font-display text-2xl font-medium">{value}</div>
      <div className="text-xs text-zinc-500 mt-0.5">{label}</div>
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        className={`block rounded-md border ${ring} bg-white p-4 hover:border-zinc-400 hover:shadow-sm transition`}
        data-testid={testid}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className={`rounded-md border ${ring} bg-white p-4`} data-testid={testid}>
      {inner}
    </div>
  );
}

function Panel({ title, icon: Icon, cta, children }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          {Icon && <Icon className="h-4 w-4 text-zinc-500" />}
          {title}
        </div>
        {cta}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

// Small "Review →" pill for the dashboard panel headers — keeps the panels
// glance-only while giving the user a one-tap drill-in into the full module
// (filtered where it makes sense via query params).
function ReviewLink({ to, testid }) {
  const { t } = useTranslation();
  return (
    <Link
      to={to}
      className="text-[11px] font-medium text-zinc-700 hover:text-ink bg-white hover:bg-zinc-50 border border-zinc-200 hover:border-zinc-400 rounded-full px-2.5 py-0.5 transition-colors inline-flex items-center gap-1"
      data-testid={testid}
    >
      {t("common.review")}
      <ArrowRight className="h-3 w-3" />
    </Link>
  );
}

function Empty({ label }) {
  return <div className="text-sm text-zinc-500">{label}</div>;
}

export function StatusBadge({ value }) {
  const { t } = useTranslation();
  const map = {
    open: "bg-zinc-100 text-zinc-700",
    waiting_approval: "bg-purple-50 text-purple-700",
    approved: "bg-sky-50 text-sky-700",
    waiting_prepay: "bg-orange-50 text-orange-700",
    // Owner rejected the requested prepayment — supplier must adjust it.
    prepay_too_high: "bg-rose-50 text-rose-700 border-rose-200",
    prepay_done: "bg-teal-50 text-teal-700",
    waiting_parts: "bg-amber-50 text-amber-700",
    in_progress: "bg-blue-50 text-blue-700",
    completed: "bg-emerald-50 text-emerald-700",
    // Owner rejected the supplier's completed work — rework cycle in motion.
    dispute: "bg-red-50 text-red-700 border-red-200",
    // `done` is the terminal status for internal crew tasks. It's labelled
    // "Completed" (same as the vendor `completed`) for a consistent UX — the
    // backend keeps them distinct, but to the user both read "Completed".
    done: "bg-emerald-50 text-emerald-700",
    // `confirmed` is the post-completion sign-off step — owner has reviewed
    // and accepted the work. Visually stronger than `completed` so crew
    // can spot at a glance which jobs are awaiting the next admin step.
    confirmed: "bg-emerald-100 text-emerald-800 border-emerald-300",
    full_payment_confirmed: "bg-green-200 text-green-900 border-green-400",
    cancelled: "bg-zinc-100 text-zinc-500",
    pending: "bg-zinc-100 text-zinc-700",
    rejected: "bg-red-50 text-red-700",
  };
  // Status label resolution falls back gracefully: known statuses use the
  // translated label from the tasks.status_* keys; anything else is rendered
  // as the underscored value with spaces (e.g. a status the BE adds before
  // the FE catches up).
  const fallback = String(value).replace(/_/g, " ");
  const label = t(`tasks.status_${value}`, { defaultValue: fallback });
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full border border-zinc-200 ${map[value] || ""}`}>
      {label}
    </span>
  );
}
